import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { BadgeInput } from '@neo/blocks/input-with-badge'
import { Tooltip } from '@neo/primitives/tooltip'
import { InfoIcon } from 'lucide-react'
import { isValidIPOrCIDR } from 'v2/shared/utils/ip-validation'
import { z } from 'zod'

export interface IPAccessControlListFormValues {
  name: string
  ip_addresses: string[]
}

const ipAccessControlListFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  ip_addresses: z
    .array(z.string().min(1, 'IP address cannot be empty'))
    .min(1, 'At least one IP address is required'),
})

export const ipAccessControlListFormFields: GenericFieldConfig<IPAccessControlListFormValues> = [
  {
    name: 'name',
    componentType: 'input',
    label: 'Name',
    inputProps: {
      placeholder: 'Enter a name to identify this IP group',
    },
    className: 'col-span-2',
    eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
    zodValidation: ipAccessControlListFormSchema.shape.name,
  },
  {
    name: 'ip_addresses',
    type: 'custom',
    label: 'IP address list',
    className: 'col-span-2',
    eventDataExtractor: (v) => v,
    valueFormatter: (value: unknown) => (Array.isArray(value) ? value : []),
    zodValidation: ipAccessControlListFormSchema.shape.ip_addresses,
    component: ({ form }) => {
      const currentIPs = form.state.values.ip_addresses || []

      const handleValidate = (ip: string, currentIPs: string[]) => {
        const ipToAdd = ip.trim()

        if (!isValidIPOrCIDR(ipToAdd))
          return 'Please enter a valid IPv4 or IPv6 address or CIDR block'

        if (currentIPs.some((existingIp) => existingIp.trim() === ipToAdd))
          return 'This IP address has already been added'
        return null
      }

      return (
        <BadgeInput
          label={
            <div className="flex items-center gap-2">
              <span>IP address list</span>
              <Tooltip content="Provide a comma-separated list of IPv4 or IPv6 addresses or CIDR notation.">
                <InfoIcon className="w-4 h-4" />
              </Tooltip>
            </div>
          }
          className="min-h-[150px]"
          value={currentIPs}
          onChange={(newIPs) => {
            form.setFieldValue('ip_addresses', newIPs)
            form.validateField('ip_addresses', 'change')
          }}
          placeholder="192.0.2.0/24"
          onValidate={handleValidate}
          acceptKeys={[',', 'Enter']}
        />
      )
    },
  },
]
